import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { HiMenu, HiX } from 'react-icons/hi'
import { createPortal } from 'react-dom'
import { useProject } from '../lib/useContent'

export default function Navbar({ content }) {
  const { project } = useProject()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const logoType = content?.logo_type || 'text'
  const logoText = content?.logo_text || 'מאיה והילה'
  const logoImage = content?.logo_image

  const activeLinks = [
    { href: '#hero', label: 'ראשי' },
    { href: '#about', label: 'מי אנחנו' },
  ]

  if (content?.gallery_images && content.gallery_images.length > 0) {
    activeLinks.push({ href: '#gallery', label: 'גלריה' })
  }

  if (project) {
    activeLinks.push({ href: '#project', label: 'הסדנה הקרובה' })
  }

  activeLinks.push({ href: '#contact', label: 'צרי קשר' })

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [mobileOpen])

  return (
    <>
      <nav
        className={`fixed top-0 right-0 left-0 w-full z-50 transition-colors duration-300 flex justify-center ${
          scrolled ? 'bg-white/95 shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="w-full max-w-6xl px-6 flex items-center justify-between h-20" dir="rtl">
          <a
            href="#hero"
            className="flex items-center gap-2"
          >
            {logoType === 'image' && logoImage ? (
              <img src={logoImage} alt={logoText} className="h-14 md:h-16 w-auto object-contain" />
            ) : (
              <span className="font-rubik font-bold text-xl text-primary-dark hover:text-primary transition-colors">
                {logoText}
              </span>
            )}
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            {activeLinks.map(link => (
              <a
                key={link.href}
                href={link.href}
                className="text-text-light hover:text-primary transition-colors text-sm font-medium"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-2.5 rounded-xl bg-primary/10 text-primary"
            aria-label="פתח תפריט"
          >
            <HiMenu size={22} />
          </button>
        </div>
      </nav>

      {/* Mobile drawer — rendered in a portal directly on body to avoid fixed-inside-fixed Safari bug */}
      {createPortal(
        <AnimatePresence>
          {mobileOpen && (
            <>
              {/* Backdrop — NO backdrop-blur, it kills Safari mobile perf */}
              <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={() => setMobileOpen(false)}
                className="fixed inset-0 bg-black/50 z-[9998] md:hidden"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              />

              {/* Drawer — translateX for GPU compositing, no blur */}
              <motion.div
                key="drawer"
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'tween', ease: [0.25, 0.1, 0.25, 1], duration: 0.3 }}
                className="fixed top-0 right-0 h-full w-[280px] bg-white z-[9999] md:hidden shadow-2xl flex flex-col p-8"
                style={{
                  height: '100dvh',
                  willChange: 'transform',
                  WebkitOverflowScrolling: 'touch',
                }}
                dir="rtl"
              >
                <div className="flex justify-between items-center mb-10">
                  <div className="flex items-center gap-2">
                    {logoType === 'image' && logoImage ? (
                      <img src={logoImage} alt={logoText} className="h-12 w-auto object-contain" />
                    ) : (
                      <span className="font-rubik font-bold text-primary text-xl">
                        {logoText}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="p-2 rounded-xl bg-gray-100 text-text"
                    aria-label="סגור תפריט"
                  >
                    <HiX size={22} />
                  </button>
                </div>

                <div className="flex flex-col gap-6">
                  {activeLinks.map((link, i) => (
                    <motion.a
                      key={link.href}
                      href={link.href}
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 + i * 0.06, duration: 0.25, ease: 'easeOut' }}
                      onClick={() => setMobileOpen(false)}
                      className="text-text-light hover:text-primary text-xl font-medium border-b border-gray-100 pb-4"
                      style={{ willChange: 'transform, opacity' }}
                    >
                      {link.label}
                    </motion.a>
                  ))}
                </div>

                <div className="mt-auto pt-6 border-t border-gray-100 text-center">
                  <p className="text-text-muted text-xs opacity-60">
                    מאיה והילה &copy; {new Date().getFullYear()}
                  </p>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  )
}
